import React from "react";
import { Manifest } from "@/common/lib/api/visitMerges";
import { COPY, formatTime } from "./copy";

const dur = (s: number | null) => {
    if (s == null) return "";
    const r = Math.round(s);
    return `${Math.floor(r / 60)}m ${String(r % 60).padStart(2, "0")}s`;
};
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const Group = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="flex flex-col">
        <p className="mt-1 text-xs font-bold text-shadow-500">{title}</p>
        <ul className="flex flex-col">{children}</ul>
    </div>
);
const Row = ({ left, right }: { left: React.ReactNode; right?: React.ReactNode }) => (
    <li className="grid grid-cols-[1fr_auto] gap-3 py-1 pl-6 text-sm text-gray-700">
        <span>{left}</span>
        <span className="text-gray-500">{right}</span>
    </li>
);

type Props = { manifest: Manifest; sameTypeDocuments: string[]; resultLinked: boolean };

export function MergeManifest({ manifest: m, sameTypeDocuments, resultLinked }: Props) {
    return (
        <div className="flex flex-col gap-1">
            {m.clips.length > 0 && (
                <Group title={plural(m.clips.length, "clip", "clips")}>
                    {m.clips.map((c) => (
                        <Row key={c.id} left={c.name} right={dur(c.durationSeconds)} />
                    ))}
                </Group>
            )}
            {m.documents.length > 0 && (
                <Group title={plural(m.documents.length, "document", "documents")}>
                    {m.documents.map((d) => (
                        <Row
                            key={d.id}
                            left={d.header}
                            right={
                                <span className="rounded-md bg-slate-100 px-2 py-px text-[11.5px] font-semibold text-slate-600">
                                    {d.exportedTo ? `Exported to ${d.exportedTo}` : "Not exported"}
                                </span>
                            }
                        />
                    ))}
                </Group>
            )}
            {m.files.length > 0 && (
                <Group title={plural(m.files.length, "file", "files")}>
                    {m.files.map((f) => (
                        <Row key={f.id} left={f.name} />
                    ))}
                </Group>
            )}
            {m.calls.length > 0 && (
                <Group title={plural(m.calls.length, "call", "calls")}>
                    {m.calls.map((c) => (
                        <Row key={c.id} left="Call" right={formatTime(c.createdAt, c.createdAt)} />
                    ))}
                </Group>
            )}
            {sameTypeDocuments.map((h) => (
                <p
                    key={h}
                    className="ml-6 mt-1 rounded-lg border border-max-200 bg-max-25 px-3 py-2 text-sm text-max-950"
                >
                    {COPY.sameTypeNote(h)}
                </p>
            ))}
            {!resultLinked && (
                <p className="mt-1 rounded-lg border border-tropicana-300 bg-tropicana-25 px-3 py-2 text-sm text-[#6B4A0A]">
                    {COPY.noLinkNote}
                </p>
            )}
        </div>
    );
}
